import {useCallback} from 'react';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import logError, {
  FeatureNameTags,
  logBreadcrumb,
} from '@/utils/errors/logError';

import {
  useConfirmSettlementMutation,
  useCreateGroupMutation,
  useJoinGroupResendMutation,
  useJoinGroupStartMutation,
  useJoinGroupVerifyMutation,
  useLazyGetGroupExpensesQuery,
  useLazyGetGroupQuery,
  useLazyGetMyDashboardQuery,
  useLazyGetUserDashboardQuery,
  useRecordAnonymousExpenseMutation,
  useRecordExpenseMutation,
} from './api';
import {
  IGetGroupDashboardResponse,
  IJoinGroupResendRequest,
  IJoinGroupStartRequest,
  IJoinGroupVerifyRequest,
  IRecordGroupExpenseRequest,
  ISettlementRequest,
} from './entities';

const fileName = 'splitBillsGroups - useGroupsRepository.ts';

const GROUPS_USER_ERRORS_API = {
  GET_GROUP: {
    TITLE: 'Error loading group',
    MESSAGE: 'We could not load the group information. Please try again.',
  },
  CREATE_GROUP: {
    TITLE: 'Error creating group',
    MESSAGE: 'We could not create the group. Please try again.',
  },
  JOIN_GROUP: {
    TITLE: 'Error joining group',
    MESSAGE: 'We could not join the group. Please try again.',
  },
  VERIFY_OTP: {
    TITLE: 'Invalid code',
    MESSAGE: 'The code is invalid or expired. Please try again.',
  },
  RESEND_OTP: {
    TITLE: 'Could not resend code',
    MESSAGE:
      'We could not send a new code. Please check your connection and try again.',
  },
  RECORD_EXPENSE: {
    TITLE: 'Error recording expense',
    MESSAGE: 'We could not record the expense. Please try again.',
  },
  SETTLEMENT: {
    TITLE: 'Error settling balance',
    MESSAGE: 'We could not complete the settlement. Please try again.',
  },
  GET_EXPENSES: {
    TITLE: 'Error loading expenses',
    MESSAGE: 'We could not load the group expenses. Please try again.',
  },
} as const;

export const useGroupsRepository = () => {
  // =========================
  // QUERIES
  // =========================

  const [
    triggerGetGroup,
    {data: groupData, isLoading: isLoadingGroup, isError: isErrorGroup},
  ] = useLazyGetGroupQuery();

  const [
    triggerGetMyDashboard,
    {
      data: myDashboard,
      isLoading: isLoadingMyDashboard,
      isError: isErrorMyDashboard,
    },
  ] = useLazyGetMyDashboardQuery();

  const [
    triggerGetUserDashboard,
    {
      data: userDashboard,
      isLoading: isLoadingUserDashboard,
      isError: isErrorUserDashboard,
    },
  ] = useLazyGetUserDashboardQuery();

  const [
    triggerGetGroupExpenses,
    {
      data: groupExpenses,
      isLoading: isLoadingGroupExpenses,
      isError: isErrorGroupExpenses,
    },
  ] = useLazyGetGroupExpensesQuery();

  // =========================
  // MUTATIONS
  // =========================

  const [triggerCreateGroup] = useCreateGroupMutation();
  const [triggerJoinGroupStart] = useJoinGroupStartMutation();
  const [triggerJoinGroupVerify] = useJoinGroupVerifyMutation();
  const [triggerJoinGroupResend] = useJoinGroupResendMutation();

  const [triggerRecordExpense] = useRecordExpenseMutation();
  const [triggerRecordAnonymousExpense] = useRecordAnonymousExpenseMutation();

  const [triggerConfirmSettlement] = useConfirmSettlementMutation();

  // =========================
  // HANDLERS
  // =========================

  const handleGetGroup = useCallback(
    async (groupId: string): Promise<IGetGroupDashboardResponse> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.getGroup`,
          type: 'query',
          message: 'Get group - API call',
          data: {groupId},
        });

        const result = await triggerGetGroup({groupId}).unwrap();
        return result;
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.GET_GROUP.TITLE,
          message: GROUPS_USER_ERRORS_API.GET_GROUP.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error fetching group',
          fileName,
          functionName: 'handleGetGroup',
          data: {groupId},
        });

        throw error;
      }
    },
    [triggerGetGroup],
  );

  const handleGetMyDashboard = useCallback(async () => {
    try {
      logBreadcrumb({
        category: `${FeatureNameTags.GROUPS}.getMyDashboard`,
        type: 'query',
        message: 'Get my dashboard - API call',
      });

      return await triggerGetMyDashboard().unwrap();
    } catch (error) {
      showSnackbar({
        variant: SnackbarType.ERROR,
        summary: 'Error loading dashboard',
        message: 'We could not load your dashboard. Please try again.',
      });

      logError(error, {
        customTitle: 'Error fetching my dashboard',
        fileName,
        functionName: 'handleGetMyDashboard',
      });

      throw error;
    }
  }, [triggerGetMyDashboard]);

  const handleGetUserDashboard = useCallback(
    async (splitUserId: string) => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.getUserDashboard`,
          type: 'query',
          message: 'Get user dashboard - API call',
          data: {splitUserId},
        });

        return await triggerGetUserDashboard({splitUserId}).unwrap();
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: 'Error loading user dashboard',
          message: 'We could not load the user dashboard. Please try again.',
        });

        logError(error, {
          customTitle: 'Error fetching user dashboard',
          fileName,
          functionName: 'handleGetUserDashboard',
          data: {splitUserId},
        });

        throw error;
      }
    },
    [triggerGetUserDashboard],
  );

  const handleCreateGroup = useCallback(
    async (params: {name: string; emoji: string}): Promise<void> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.createGroup`,
          type: 'query',
          message: 'Create group - API call',
          data: params,
        });

        await triggerCreateGroup(params).unwrap();
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.CREATE_GROUP.TITLE,
          message: GROUPS_USER_ERRORS_API.CREATE_GROUP.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error creating group',
          fileName,
          functionName: 'handleCreateGroup',
          data: params,
        });

        throw error;
      }
    },
    [triggerCreateGroup],
  );

  const handleJoinGroupStart = useCallback(
    async (params: IJoinGroupStartRequest) => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.joinGroupStart`,
          type: 'query',
          message: 'Join group start - API call',
          data: {
            group_id: params.group_id,
            phone_number: params.phone_number,
            name: params.display_name,
            split_user_id: params.split_user_id,
          },
        });

        return await triggerJoinGroupStart(params).unwrap();
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.JOIN_GROUP.TITLE,
          message: GROUPS_USER_ERRORS_API.JOIN_GROUP.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error starting join group',
          fileName,
          functionName: 'handleJoinGroupStart',
          data: params,
        });

        throw error;
      }
    },
    [triggerJoinGroupStart],
  );

  const handleJoinGroupVerify = useCallback(
    async (params: IJoinGroupVerifyRequest) => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.joinGroupVerify`,
          type: 'query',
          message: 'Verify join group OTP - API call',
          data: {
            group_id: params.group_id,
            split_user_id: params.split_user_id,
          },
        });

        return await triggerJoinGroupVerify(params).unwrap();
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.VERIFY_OTP.TITLE,
          message: GROUPS_USER_ERRORS_API.VERIFY_OTP.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error verifying OTP',
          fileName,
          functionName: 'handleJoinGroupVerify',
          data: params,
        });

        throw error;
      }
    },
    [triggerJoinGroupVerify],
  );

  const handleJoinGroupResend = useCallback(
    async (params: IJoinGroupResendRequest): Promise<void> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.useGroupsRepository.handleJoinGroupResend`,
          type: 'query',
          message: 'Resend participant OTP - API call',
          data: {
            group_id: params.group_id,
            split_user_id: params.split_user_id,
          },
        });
        const result = await triggerJoinGroupResend(params).unwrap();
        return result;
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.RESEND_OTP.TITLE,
          message: GROUPS_USER_ERRORS_API.RESEND_OTP.MESSAGE,
        });
        logError(error, {
          customTitle: 'Groups - Error resending OTP',
          fileName,
          functionName: 'handleResendOtp',
          data: {
            group_id: params.group_id,
            split_user_id: params.split_user_id,
          },
        });
        throw error;
      }
    },
    [triggerJoinGroupResend],
  );

  const handleRecordExpense = useCallback(
    async (groupId: string, body: IRecordGroupExpenseRequest) => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.recordExpense`,
          type: 'query',
          message: 'Record expense - API call',
          data: {groupId, description: body.description},
        });

        const result = await triggerRecordExpense({groupId, body}).unwrap();
        return result;
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.RECORD_EXPENSE.TITLE,
          message: GROUPS_USER_ERRORS_API.RECORD_EXPENSE.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error recording expense',
          fileName,
          functionName: 'handleRecordExpense',
          data: {groupId, body},
        });

        throw error;
      }
    },
    [triggerRecordExpense],
  );

  const handleRecordAnonymousExpense = useCallback(
    async (groupId: string, body: IRecordGroupExpenseRequest) => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.recordAnonymousExpense`,
          type: 'query',
          message: 'Record anonymous expense - API call',
          data: {groupId, description: body.description},
        });

        const result = await triggerRecordAnonymousExpense({
          groupId,
          body,
        }).unwrap();
        return result;
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.RECORD_EXPENSE.TITLE,
          message: GROUPS_USER_ERRORS_API.RECORD_EXPENSE.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error recording anonymous expense',
          fileName,
          functionName: 'handleRecordAnonymousExpense',
          data: {groupId, body},
        });

        throw error;
      }
    },
    [triggerRecordAnonymousExpense],
  );

  const handleConfirmSettlement = useCallback(
    async (groupId: string, params: ISettlementRequest): Promise<void> => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.confirmSettlement`,
          type: 'query',
          message: 'Confirm settlement - API call',
          data: params,
        });

        await triggerConfirmSettlement({groupId, body: params}).unwrap();
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.SETTLEMENT.TITLE,
          message: GROUPS_USER_ERRORS_API.SETTLEMENT.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error confirming settlement',
          fileName,
          functionName: 'handleConfirmSettlement',
          data: params,
        });

        throw error;
      }
    },
    [triggerConfirmSettlement],
  );

  const handleGetGroupExpenses = useCallback(
    async (groupId: string, limit?: number, lastKey?: string) => {
      try {
        logBreadcrumb({
          category: `${FeatureNameTags.GROUPS}.getGroupExpenses`,
          type: 'query',
          message: 'Get group expenses - API call',
          data: {groupId, limit, lastKey},
        });

        return await triggerGetGroupExpenses({
          groupId,
          limit,
          lastKey,
        }).unwrap();
      } catch (error) {
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: GROUPS_USER_ERRORS_API.GET_EXPENSES.TITLE,
          message: GROUPS_USER_ERRORS_API.GET_EXPENSES.MESSAGE,
        });

        logError(error, {
          customTitle: 'Error fetching group expenses',
          fileName,
          functionName: 'handleGetGroupExpenses',
          data: {groupId, limit, lastKey},
        });

        throw error;
      }
    },
    [triggerGetGroupExpenses],
  );

  return {
    // data
    groupData,
    userDashboard,
    isLoadingGroup,
    myDashboard,
    isLoadingUserDashboard,
    isLoadingMyDashboard,
    isErrorGroup,
    isErrorUserDashboard,
    isErrorMyDashboard,
    groupExpenses,
    isLoadingGroupExpenses,
    isErrorGroupExpenses,

    // handlers
    handleGetMyDashboard,
    handleGetUserDashboard,
    handleGetGroup,
    handleCreateGroup,
    handleJoinGroupStart,
    handleJoinGroupVerify,
    handleJoinGroupResend,
    handleRecordExpense,
    handleRecordAnonymousExpense,
    handleConfirmSettlement,
    handleGetGroupExpenses,
  };
};
